import React, { useState } from "react";
import { PartnerFormShell, Row, Field, Select, Pill } from "./PartnerFormShell";
import { Printer, Factory, Target } from "lucide-react";

const PROCESSES = [
  { code: "offset_sheet",   label: "Sheet-fed offset" },
  { code: "offset_web",     label: "Web offset" },
  { code: "digital_toner",  label: "Digital (toner)" },
  { code: "digital_inkjet", label: "Digital (inkjet)" },
  { code: "flexo",          label: "Flexo" },
  { code: "gravure",        label: "Gravure" },
  { code: "screen",         label: "Screen" },
  { code: "corrugation",    label: "Corrugation" },
  { code: "flexible_pkg",   label: "Flexible packaging" },
  { code: "label",          label: "Labels / stickers" },
];
const FINISHINGS = [
  "Lamination", "UV coating", "Spot UV", "Foil stamping",
  "Embossing", "Die-cutting", "Binding", "Pouching",
];

export default function PartnerPrinter() {
  const [extra, setExtra] = useState({
    processes: [], finishings: [],
    sheet_size_max: "", capacity_monthly: "",
    gsm_range: "", min_order_qty: "",
    iso_9001: false, fsc: false, brc: false, halal: false,
  });

  const toggleArr = (key, val) => setExtra(e => ({
    ...e,
    [key]: e[key].includes(val) ? e[key].filter(c => c !== val) : [...e[key], val],
  }));
  const setField = (e) => {
    const { name, value, type, checked } = e.target;
    setExtra(x => ({ ...x, [name]: type === "checkbox" ? checked : value }));
  };

  return (
    <PartnerFormShell
      partnerType="printer"
      testIdPrefix="partner-printer"
      accentHue="#2563EB"
      title="Join as a Printer / Converter"
      lede="Sheet-fed offset, digital, flexo, corrugation, flexible packaging, labels — if you print it, we route sealed-bid auctions from brand buyers straight to your inbox with escrow protection and blind-shipped dispatch."
      benefits={[
        { icon: <Target size={16}/>,  title: "Pre-matched demand",   body: "Only RFQs that match your capability tags land in your inbox — no spray-and-pray." },
        { icon: <Factory size={16}/>, title: "You stay anonymous",   body: "Your name, address and GSTIN are hidden from buyers." },
        { icon: <Printer size={16}/>, title: "Payout via Razorpay",  body: "Escrow-held funds are wired to your bank automatically on delivery confirmation." },
      ]}
      extraState={extra}
      validateExtra={() => extra.processes.length === 0 ? "Please tick at least one printing process." : null}
      extraFields={
        <>
          <div className="pf-group">
            <label className="pf-label">Printing processes you offer *</label>
            <p className="pf-help">Pick everything you run in-house regularly. We tag your capacity by process to match RFQs.</p>
            <div className="pf-pillrow">
              {PROCESSES.map(p => (
                <Pill
                  key={p.code} label={p.label}
                  active={extra.processes.includes(p.code)}
                  onClick={() => toggleArr("processes", p.code)}
                  testid={`partner-printer-process-${p.code}`}
                />
              ))}
            </div>
          </div>
          <Row>
            <Field label="Largest sheet size (max)" name="sheet_size_max" value={extra.sheet_size_max}
                   onChange={setField} placeholder="e.g. 28 × 40 inches" maxLength={60}
                   testid="partner-printer-sheet-size"/>
            <Field label="Monthly capacity" name="capacity_monthly" value={extra.capacity_monthly}
                   onChange={setField} placeholder="e.g. 50 lakh impressions / 15 lakh cartons" maxLength={80}
                   testid="partner-printer-capacity"/>
          </Row>
          <Row>
            <Field label="Substrate GSM range" name="gsm_range" value={extra.gsm_range}
                   onChange={setField} placeholder="e.g. 80 – 450 gsm" maxLength={60}
                   testid="partner-printer-gsm"/>
            <Field label="Minimum order quantity" name="min_order_qty" value={extra.min_order_qty}
                   onChange={setField} placeholder="e.g. 500 cartons / 1000 sheets" maxLength={60}
                   testid="partner-printer-moq"/>
          </Row>
          <div className="pf-group">
            <label className="pf-label">In-house finishing</label>
            <div className="pf-pillrow">
              {FINISHINGS.map(f => (
                <Pill
                  key={f} label={f}
                  active={extra.finishings.includes(f)}
                  onClick={() => toggleArr("finishings", f)}
                  testid={`partner-printer-finish-${f.replace(/\s/g,'-').toLowerCase()}`}
                />
              ))}
            </div>
          </div>
          <div className="pf-group">
            <label className="pf-label">Certifications (tick where held)</label>
            <div className="pf-checkrow">
              {[["iso_9001","ISO 9001"],["fsc","FSC"],["brc","BRC Packaging"],["halal","Halal"]].map(([k,l]) => (
                <label key={k} className="pf-check">
                  <input type="checkbox" name={k} checked={!!extra[k]} onChange={setField}
                         data-testid={`partner-printer-cert-${k}`}/>
                  <span>{l}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      }
    />
  );
}
